// Pagination — the single, uniform pager used across every list surface
// (precedents, upload queue, …). Presentational + controlled: the parent owns
// page state (usually via usePagination), so this same control drives both
// client-sliced and, later, server-paged lists. Renders nothing for a single
// page. Styles live in ds-primitives.css (design-system tokens only).
export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Optional "1–25 of 137 precedents" range label inputs. */
  total?: number;
  rangeStart?: number;
  rangeEnd?: number;
  /** Singular noun for the range label (e.g. "precedent" → "… precedents"). */
  itemLabel?: string;
  className?: string;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  total,
  rangeStart,
  rangeEnd,
  itemLabel,
  className,
}: PaginationProps): JSX.Element | null {
  if (pageCount <= 1) return null; // one page → no controls

  const showRange =
    total !== undefined && rangeStart !== undefined && rangeEnd !== undefined;

  return (
    <nav
      className={className ? `ds-pg ${className}` : "ds-pg"}
      role="navigation"
      aria-label="Pagination"
      data-testid="pagination"
    >
      {showRange && (
        <span className="ds-pg__range" data-testid="pagination-range">
          {rangeStart}–{rangeEnd} of {total}
          {itemLabel !== undefined ? ` ${pluralize(total, itemLabel)}` : ""}
        </span>
      )}
      <div className="ds-pg__controls">
        <button
          type="button"
          className="ds-pg__btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          ‹ Prev
        </button>
        {pageWindow(page, pageCount).map((p, i) =>
          p === ELLIPSIS ? (
            <span key={`gap-${i}`} className="ds-pg__gap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={p === page ? "ds-pg__num is-active" : "ds-pg__num"}
              onClick={() => onPageChange(p)}
              aria-label={`Page ${p}`}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          className="ds-pg__btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          Next ›
        </button>
      </div>
    </nav>
  );
}

const ELLIPSIS = "…" as const;

// First page, last page, and the current page ±1, with ellipses bridging gaps.
function pageWindow(current: number, count: number): (number | typeof ELLIPSIS)[] {
  const wanted = new Set<number>([1, count, current - 1, current, current + 1]);
  const pages = [...wanted]
    .filter((n) => n >= 1 && n <= count)
    .sort((a, b) => a - b);
  const out: (number | typeof ELLIPSIS)[] = [];
  let prev = 0;
  for (const p of pages) {
    if (p - prev > 1) out.push(ELLIPSIS);
    out.push(p);
    prev = p;
  }
  return out;
}

function pluralize(n: number, singular: string): string {
  return n === 1 ? singular : `${singular}s`;
}
